import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import ProductFilters from './components/ProductFilters';
import ProductGrid from './components/ProductGrid';
import ProductDetailPanel from './components/ProductDetailPanel';
import ProductModal from './components/ProductModal';
import { productService } from '../../services/productService';
import { Package, CheckCircle, XCircle, Plus } from 'lucide-react';
import Icon from '../../components/AppIcon';





const KPICard = ({ title, value, subtitle, icon: Icon, iconColor, valueColor }) => (
  <div className="bg-[#1A1A1A] rounded-xl border border-white/8 p-4 shadow-sm hover:border-white/15 transition-all duration-200 flex flex-col justify-between min-h-[110px]">
    <div className="flex items-start justify-between mb-2">
      <span className="text-xs font-oranienbaum text-white/50 leading-tight pr-2">{title}</span>
      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${iconColor}18` }}>
        <Icon size={16} style={{ color: iconColor }} />
      </div>
    </div>
    <p className="text-xl font-bold font-oranienbaum leading-tight" style={{ color: valueColor || '#ffffff' }}>
      {value}
    </p>
    {subtitle && (
      <p className="text-[10px] font-oranienbaum text-white/35 mt-1 truncate">{subtitle}</p>
    )}
  </div>
);

const ProductManagementSimple = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all'
  });

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await productService?.getAll();
      setProducts(data || []);
      setFilteredProducts(data || []);
    } catch (err) {
      console.error('Error loading products:', err);
      setError(err?.message || 'Error al cargar productos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    applyFilters();
  }, [filters, products]);

  const applyFilters = () => {
    let filtered = [...products];
    if (filters?.search) {
      const searchLower = filters?.search?.toLowerCase();
      filtered = filtered?.filter((product) =>
        product?.nombre?.toLowerCase()?.includes(searchLower)
      );
    }
    if (filters?.status !== 'all') {
      const isActive = filters?.status === 'active';
      filtered = filtered?.filter((product) => product?.activo === isActive);
    }
    setFilteredProducts(filtered);
  };

  const handleFilterChange = (key, value) => {
    if (key === 'reset') {
      setFilters({ search: '', status: 'all' });
    } else {
      setFilters((prev) => ({ ...prev, [key]: value }));
    }
  };

  const handleSelectProduct = (product) => setSelectedProduct(product);

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setShowFormModal(true);
  };

  const handleToggleStatus = async (product) => {
    try {
      await productService?.update(product?.id, { ...product, activo: !product?.activo });
      await loadProducts();
      if (selectedProduct?.id === product?.id) {
        const updated = await productService?.getById(product?.id);
        setSelectedProduct(updated);
      }
    } catch (err) {
      console.error('Error toggling product status:', err);
      alert(err?.message || 'Error al cambiar estado del producto');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este producto?')) return;
    try {
      await productService?.delete(productId);
      await loadProducts();
      if (selectedProduct?.id === productId) setSelectedProduct(null);
    } catch (err) {
      console.error('Error deleting product:', err);
      alert(err?.message || 'Error al eliminar producto');
    }
  };

  const handleAddProduct = () => {
    setEditingProduct(null);
    setShowFormModal(true);
  };

  const handleCloseModal = () => {
    setShowFormModal(false);
    setEditingProduct(null);
  };

  const handleSaveSuccess = async () => {
    await loadProducts();
    setShowFormModal(false);
    setEditingProduct(null);
  };

  const totalProducts = products?.length || 0;
  const activeProducts = products?.filter((p) => p?.activo)?.length || 0;
  const inactiveProducts = products?.filter((p) => !p?.activo)?.length || 0;
  const activeRate = totalProducts > 0 ? ((activeProducts / totalProducts) * 100)?.toFixed(0) : 0;

  const kpis = [
    { title: 'Total Productos', value: totalProducts, subtitle: 'Catálogo completo', icon: Package, iconColor: '#C9A24D', valueColor: '#C9A24D' },
    { title: 'Productos Activos', value: activeProducts, subtitle: `${activeRate}% del catálogo`, icon: CheckCircle, iconColor: '#3A7F6B', valueColor: '#3A7F6B' },
    { title: 'Productos Inactivos', value: inactiveProducts, subtitle: 'Fuera de servicio', icon: XCircle, iconColor: '#E05252', valueColor: '#E05252' },
    { title: 'Resultados Filtrados', value: filteredProducts?.length || 0, subtitle: 'Según filtros activos', icon: Package, iconColor: '#8097D6', valueColor: '#8097D6' },
  ];

  return (
    <>
      <Helmet>
        <title>Gestión de Productos - Las Dos Doncellas</title>
      </Helmet>
      <div className="min-h-screen bg-[#111111] font-oranienbaum">
        <Header />
        <main className="pt-14">
          <div className="max-w-[1760px] mx-auto px-4 sm:px-6 lg:px-8 py-5">

            <BreadcrumbNavigation />

            {/* Page Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <div>
                <h1 className="text-2xl lg:text-3xl font-oranienbaum font-bold text-[#D6EF4E]">
                  Gestión de Productos
                </h1>
                <p className="font-oranienbaum text-white/40 text-sm mt-0.5">
                  Administración del catálogo de productos de Sala Corte
                </p>
              </div>
              <button
                onClick={handleAddProduct}
                className="flex items-center gap-2 px-4 py-2 bg-[#D6EF4E] text-[#111111] rounded-lg font-oranienbaum font-bold text-sm hover:bg-[#c8e040] transition-colors flex-shrink-0"
              >
                <Plus size={16} />
                Añadir Producto
              </button>
            </div>

            {/* Error */}
            {error && (
              <div className="mb-4 bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg">
                <p className="font-oranienbaum text-sm">{error}</p>
              </div>
            )}

            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              {kpis?.map((kpi) => (
                <KPICard key={kpi?.title} {...kpi} />
              ))}
            </div>

            {/* Loading */}
            {loading && (
              <div className="bg-[#1A1A1A] rounded-xl border border-white/8 p-12 text-center">
                <p className="text-white/50 font-oranienbaum text-lg">Cargando productos...</p>
              </div>
            )}

            {/* Main Content */}
            {!loading && !error && (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                {/* Filters */}
                <div className="lg:col-span-3">
                  <ProductFilters filters={filters} onFilterChange={handleFilterChange} />
                </div>
                {/* Table */}
                <div className={`${selectedProduct ? 'lg:col-span-6' : 'lg:col-span-9'} space-y-4`}>
                  <ProductGrid
                    products={filteredProducts}
                    selectedProduct={selectedProduct}
                    onSelectProduct={handleSelectProduct}
                    onEditProduct={handleEditProduct}
                    onToggleStatus={handleToggleStatus}
                    onDeleteProduct={handleDeleteProduct}
                  />
                </div>
                {/* Detail Panel */}
                {selectedProduct && (
                  <div className="lg:col-span-3">
                    <ProductDetailPanel
                      product={selectedProduct}
                      onEdit={handleEditProduct}
                      onToggleStatus={handleToggleStatus}
                      onDelete={handleDeleteProduct}
                    />
                  </div>
                )}
              </div>
            )}

          </div>
        </main>
      </div>
      {showFormModal && (
        <ProductModal
          product={editingProduct}
          onClose={handleCloseModal}
          onSaveSuccess={handleSaveSuccess}
        />
      )}
    </>
  );
};

export default ProductManagementSimple;